import { useState, type PropsWithChildren } from "react";
import {
  ChevronDown,
  CircleHelp,
  LayoutDashboard,
  LogOut,
  Menu,
  Plus,
  TicketCheck,
  UserCog,
  UsersRound,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { NavLink, useRouter } from "../lib/router";
import { cn, initials } from "../lib/utils";

const navItems = [
  { to: "/", label: "Visão geral", icon: LayoutDashboard, end: true },
  { to: "/chamados", label: "Chamados", icon: TicketCheck, end: true },
];

export function AppShell({ children }: PropsWithChildren) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { session, signOut } = useAuth();
  const { navigate, pathname } = useRouter();
  const ticketDetail = pathname.match(/^\/chamados\/(\d+)$/);
  const topbarContext =
    pathname === "/"
      ? { eyebrow: "Central de atendimento", title: "Visão geral", icon: LayoutDashboard }
      : pathname === "/chamados/novo"
        ? { eyebrow: "Atendimento", title: "Novo chamado", icon: Plus }
        : pathname === "/usuarios"
          ? { eyebrow: "Administração", title: "Gestão de usuários", icon: UsersRound }
        : pathname === "/acessos"
          ? { eyebrow: "Administração", title: "Solicitação de acesso", icon: UserCog }
        : ticketDetail
          ? { eyebrow: "Atendimento", title: `Chamado #${ticketDetail[1]}`, icon: TicketCheck }
          : { eyebrow: "Atendimento", title: "Chamados", icon: TicketCheck };
  const TopbarContextIcon = topbarContext.icon;

  function logout() {
    signOut();
    navigate("/login", { replace: true });
  }

  const sidebar = (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <img
          className="sidebar-logo"
          src="/brand/mira-logo-white.svg"
          alt="Mira Sistemas"
        />
        <span className="sidebar-product">Helpdesk</span>
        <button
          className="ml-auto rounded-lg p-2 text-white/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Fechar menu"
        >
          <X className="size-5" />
        </button>
      </div>

      <div className="sidebar-company">
        <span>Ambiente atual</span>
        <strong title={session?.empresa}>{session?.empresa || "Sua empresa"}</strong>
        <i />
      </div>

      <nav className="sidebar-nav" aria-label="Navegação principal">
        <p>Atendimento</p>
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cn(
                "sidebar-link",
                (to === "/chamados"
                  ? isActive || /^\/chamados\/\d+$/.test(pathname)
                  : isActive) && "active",
              )
            }
          >
            <Icon className="size-[18px]" />
            <span>{label}</span>
          </NavLink>
        ))}
        {(session?.podeGerenciarUsuarios || session?.podeSolicitarAcesso) && (
          <NavLink
            to="/acessos"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) => cn("sidebar-link", isActive && "active")}
          >
            <UserCog className="size-[18px]" />
            <span>Solicitação de acesso</span>
          </NavLink>
        )}
        <NavLink
          to="/chamados/novo"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) => cn("sidebar-link", isActive && "active")}
        >
          <Plus className="size-[18px]" />
          <span>Abrir chamado</span>
        </NavLink>
        {session?.podeGerenciarUsuarios && session?.codigoEmpresa === 52 && (
          <>
            <p className="sidebar-nav-section">Administração</p>
            <NavLink
              to="/usuarios"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => cn("sidebar-link", isActive && "active")}
            >
              <UsersRound className="size-[18px]" />
              <span>Usuários</span>
            </NavLink>
          </>
        )}
      </nav>

      <div className="sidebar-help">
        <span className="sidebar-help-icon">
          <CircleHelp className="size-5" />
        </span>
        <div>
          <strong>Precisa de ajuda?</strong>
          <p>Nossa equipe acompanha cada etapa.</p>
        </div>
      </div>

      <div className="sidebar-footer">
        <div className="avatar avatar--dark">{initials(session?.login)}</div>
        <div className="min-w-0">
          <strong>{session?.login || "Usuário"}</strong>
          <span>Conta de atendimento</span>
        </div>
        <button onClick={logout} title="Sair" aria-label="Sair">
          <LogOut className="size-4" />
        </button>
      </div>
    </aside>
  );

  return (
    <div className="app-frame">
      <div className="hidden lg:block">{sidebar}</div>
      {mobileOpen && (
        <div className="mobile-menu-layer" onClick={() => setMobileOpen(false)}>
          <div onClick={(event) => event.stopPropagation()}>{sidebar}</div>
        </div>
      )}

      <div className="app-content">
        <header className="topbar">
          <button
            className="topbar-icon lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="size-5" />
          </button>
          <img
            className="topbar-mobile-logo lg:hidden"
            src="/brand/mira-symbol.svg"
            alt="Mira Sistemas"
          />

          <div className="topbar-context" aria-label="Página atual">
            <span className="topbar-context-icon">
              <TopbarContextIcon />
            </span>
            <span className="topbar-context-copy">
              <small>{topbarContext.eyebrow}</small>
              <strong>{topbarContext.title}</strong>
            </span>
          </div>

          <div className="topbar-actions">
            <div className="relative">
              <button
                className="profile-trigger"
                onClick={() => setProfileOpen((current) => !current)}
                aria-expanded={profileOpen}
              >
                <span className="avatar">{initials(session?.login)}</span>
                <span className="hidden min-w-0 text-left sm:block">
                  <strong>{session?.login}</strong>
                  <small>{session?.empresa}</small>
                </span>
                <ChevronDown className="size-4 text-slate-400" />
              </button>
              {profileOpen && (
                <div className="profile-menu">
                  <p>Sessão protegida</p>
                  <button onClick={logout}>
                    <LogOut className="size-4" />
                    Sair do sistema
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
